import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Heart, MessageCircleHeart, Users, CheckCircle2, RefreshCw, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/api';

const PAGE_SIZE = 25;

const TRIGGER_LABELS = {
  manual: 'Manual',
  post_analysis: 'After analysis',
  post_inspection: 'After inspection',
};

function Hearts({ value }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((v) => (
        <Heart key={v} className={`w-3.5 h-3.5 ${v <= value ? 'text-marker fill-marker': 'text-ink-muted'}`} />
      ))}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-surface border border-line rounded-md p-4">
      <div className="flex items-center gap-2 text-ink-muted">
        <Icon className="w-4 h-4" />
        <p className="text-xs font-semibold uppercase tracking-[0.08em]">{label}</p>
      </div>
      <p className="text-2xl font-bold text-ink mt-1 font-mono">{value}</p>
      {sub && <p className="text-xs text-ink-muted mt-0.5">{sub}</p>}
    </div>
  );
}

export default function AdminFeedbackPage() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [ratingFilter, setRatingFilter] = useState('all');
  const [triggerFilter, setTriggerFilter] = useState('all');
  const [daysFilter, setDaysFilter] = useState('all');

  const fetchData = useCallback(async (nextOffset = 0) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: nextOffset };
      if (ratingFilter !== 'all') params.rating = Number(ratingFilter);
      if (triggerFilter !== 'all') params.trigger_source = triggerFilter;
      if (daysFilter !== 'all') params.days = Number(daysFilter);
      const [summaryRes, listRes] = await Promise.all([
        axios.get(`${API}/admin/feedback/summary`),
        axios.get(`${API}/admin/feedback`, { params }),
      ]);
      setSummary(summaryRes.data);
      setItems(listRes.data.items);
      setTotal(listRes.data.total);
      setOffset(nextOffset);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [ratingFilter, triggerFilter, daysFilter]);

  useEffect(() => { fetchData(0); }, [fetchData]);

  const distribution = summary?.rating_distribution || {};
  const maxDist = Math.max(1, ...Object.values(distribution).map(Number));

  return (
    <DashboardLayout>
      <div data-testid="admin-feedback-page" className="space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-ink font-display">User Feedback</h1>
            <p className="text-sm text-ink-muted mt-1">What testers rated, where they got stuck, and what they need next.</p>
          </div>
          <Button onClick={() => fetchData(offset)} variant="outline" className="h-9 border-line-strong text-ink">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Heart}
            label="Average rating"
            value={summary?.avg_rating != null ? `${summary.avg_rating}/5` : '—'}
          />
          <StatCard icon={MessageCircleHeart} label="Submissions" value={summary?.total ?? '—'} />
          <StatCard icon={Users} label="Unique testers" value={summary?.unique_users ?? '—'} />
          <StatCard
            icon={CheckCircle2}
            label="Completion rate"
            value={summary?.completion_rate != null ? `${Math.round(summary.completion_rate * 100)}%` : '—'}
            sub="All three questions answered"
          />
        </div>

        {/* Rating distribution */}
        <div className="bg-surface border border-line rounded-md p-5">
          <h2 className="m-0 mb-3 font-display text-[22px] font-bold leading-7">Rating distribution</h2>
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((r) => (
              <div key={r} className="flex items-center gap-3">
                <span className="w-16 shrink-0"><Hearts value={r} /></span>
                <div className="flex-1 h-4 bg-sunken rounded-full overflow-hidden">
                  <div
                    className="h-full bg-sev-high rounded-full transition-all"
                    style={{ width: `${(Number(distribution[r] || 0) / maxDist) * 100}%` }}
                  />
                </div>
                <span className="w-8 text-right text-sm font-semibold text-ink font-mono">
                  {distribution[r] || 0}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Feed */}
        <div className="bg-surface border border-line rounded-md">
          <div className="p-4 border-b border-line flex items-center gap-2 flex-wrap">
            <h2 className="m-0 mr-auto font-display text-[22px] font-bold leading-7">Submissions <span className="font-mono">{total}</span></h2>
            <Select value={ratingFilter} onValueChange={setRatingFilter}>
              <SelectTrigger className="w-[130px] h-9 bg-sunken border-line text-sm" data-testid="filter-rating">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface">
                <SelectItem value="all">All ratings</SelectItem>
                {[5, 4, 3, 2, 1].map((r) => (
                  <SelectItem key={r} value={String(r)}>{r} heart{r > 1 ? 's' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={triggerFilter} onValueChange={setTriggerFilter}>
              <SelectTrigger className="w-[160px] h-9 bg-sunken border-line text-sm" data-testid="filter-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface">
                <SelectItem value="all">All triggers</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="post_analysis">After analysis</SelectItem>
                <SelectItem value="post_inspection">After inspection</SelectItem>
              </SelectContent>
            </Select>
            <Select value={daysFilter} onValueChange={setDaysFilter}>
              <SelectTrigger className="w-[130px] h-9 bg-sunken border-line text-sm" data-testid="filter-days">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface">
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-ink-muted" /></div>
          ) : !items.length ? (
            <p className="p-6 text-sm text-ink-muted">No feedback yet for these filters. Share the app with testers and answers will show up here.</p>
          ) : (
            <ul className="divide-y divide-line">
              {items.map((item) => (
                <li key={item.id} className="p-4" data-testid="feedback-item">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Hearts value={item.rating} />
                    <span className="text-sm font-semibold text-ink">{item.user_email}</span>
                    <span className="text-xs font-semibold uppercase tracking-[0.08em] px-2 py-0.5 rounded-full bg-marker-soft text-ink border border-line-strong">
                      {TRIGGER_LABELS[item.trigger_source] || item.trigger_source}
                    </span>
                    {item.property_name && (
                      <span className="text-xs text-ink-muted">re: {item.property_name}</span>
                    )}
                    <span className="text-xs text-ink-muted ml-auto">
                      {new Date(item.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-2 grid md:grid-cols-3 gap-3">
                    {[
                      ['Stuck with', item.stuck_with],
                      ['Must-have', item.must_have],
                      ['Not needed', item.not_needed],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-md bg-sunken border border-line p-2.5">
                        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-ink-muted">{label}</p>
                        <p className={`text-sm mt-0.5 ${value ? 'text-ink': 'text-ink-muted italic'}`}>
                          {value || 'No answer'}
                        </p>
                      </div>
                    ))}
                  </div>
                  {(item.route || item.metadata?.device_class) && (
                    <p className="text-xs text-ink-muted mt-2">
                      {item.route && <>Page: {item.route}</>}
                      {item.metadata?.device_class && <> · Device: {item.metadata.device_class}</>}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {total > PAGE_SIZE && (
            <div className="p-4 border-t border-line flex items-center justify-between">
              <p className="text-xs text-ink-muted">
                Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={offset === 0 || loading}
                  onClick={() => fetchData(Math.max(0, offset - PAGE_SIZE))}
                  className="h-8 text-xs border-line-strong"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  disabled={offset + PAGE_SIZE >= total || loading}
                  onClick={() => fetchData(offset + PAGE_SIZE)}
                  className="h-8 text-xs border-line-strong"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
